import { Download, FileText, FileAudio, FileVideo } from 'lucide-react'
import type { ChatAttachment } from '../models/chat.model'

/**
 * Per-kind asset bubble used inside a chat message.
 *
 *   image    → tap-to-open preview (object-cover thumbnail)
 *   audio    → native <audio controls> with file name above
 *   video    → native <video controls> sized to the bubble
 *   document → icon + file name + size + download button
 *
 * The component is intentionally dumb: it receives a fully-resolved
 * attachment (incl. `url`) and renders it. The caller decides whether the
 * URL is an object URL (mock) or a real CDN link (when the backend lands).
 */

interface ChatAttachmentBubbleProps {
  attachment: ChatAttachment
  isMe: boolean
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
}

export function ChatAttachmentBubble({ attachment, isMe }: ChatAttachmentBubbleProps) {
  const { kind, url, name, size } = attachment
  const bubbleSide = isMe ? 'rounded-br-sm' : 'rounded-bl-sm'

  if (kind === 'image') {
    return (
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className={`block overflow-hidden rounded-2xl ${bubbleSide} max-w-[260px]`}
      >
        <img src={url} alt={name} className="w-full h-auto max-h-[240px] object-cover" />
        <div className="bg-slate-100 dark:bg-muted px-3 py-1.5 text-[10px] text-slate-500 dark:text-muted-foreground truncate">
          {name}
        </div>
      </a>
    )
  }

  if (kind === 'video') {
    return (
      <div
        className={`overflow-hidden rounded-2xl ${bubbleSide} bg-slate-100 dark:bg-muted max-w-[280px]`}
      >
        <video
          src={url}
          controls
          playsInline
          className="w-full h-auto max-h-[260px] bg-black"
        />
        <div className="px-3 py-1.5 text-[10px] text-slate-500 dark:text-muted-foreground truncate flex items-center gap-1.5">
          <FileVideo className="w-3 h-3" />
          <span className="truncate">{name}</span>
          <span className="ml-auto shrink-0">{formatBytes(size)}</span>
        </div>
      </div>
    )
  }

  if (kind === 'audio') {
    return (
      <div
        className={`rounded-2xl ${bubbleSide} bg-slate-100 dark:bg-muted px-3 py-2 max-w-[280px] w-full`}
      >
        <div className="flex items-center gap-2 text-xs text-slate-700 dark:text-foreground mb-1.5">
          <FileAudio className="w-3.5 h-3.5 text-amber-500" />
          <span className="truncate flex-1">{name}</span>
          <span className="text-[10px] text-slate-400 dark:text-muted-foreground shrink-0">
            {formatBytes(size)}
          </span>
        </div>
        <audio src={url} controls className="w-full h-8" />
      </div>
    )
  }

  // document
  return (
    <a
      href={url}
      download={name}
      target="_blank"
      rel="noreferrer"
      className={`flex items-center gap-3 rounded-2xl ${bubbleSide} bg-slate-100 dark:bg-muted px-3 py-2.5 max-w-[280px] hover:bg-slate-200 dark:hover:bg-muted/80 transition-colors`}
    >
      <span className="w-9 h-9 rounded-lg bg-sky-500/15 text-sky-500 flex items-center justify-center shrink-0">
        <FileText className="w-4 h-4" />
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-xs font-medium text-slate-800 dark:text-foreground truncate">
          {name}
        </span>
        <span className="block text-[10px] text-slate-400 dark:text-muted-foreground">
          {formatBytes(size)}
        </span>
      </span>
      <Download className="w-3.5 h-3.5 text-slate-400 dark:text-muted-foreground shrink-0" />
    </a>
  )
}
